'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type { Motion } from 'components/science/landing-v2/GradientShader';
import {
  CARD_SHADER,
  textureById,
} from 'components/science/landing-v2/gradientTextures';
import { useHoverShaders } from 'components/science/landing-v2/useHoverShaders';
import { OutletMark } from './OutletMark';
import { PRESS_ITEMS } from './pressItems';
import styles from './RingProUsPress.module.css';

/** Shown at max-width 767px. Tablet and desktop still render every PRESS_ITEMS entry. */
const PHONE_PRESS_IDS = new Set([
  'engadgetCase',
  'techAdvisorVerdict',
  'wareableStamina',
]);

const GradientShader = dynamic(
  () => import('components/science/landing-v2/GradientShader'),
  { ssr: false },
);

/* Same hover as why-ultrahuman Research / science-lp ValidatedMetrics: each
   card fades in its own dark dither gradient and the copy flips to white.
   The four gradients cycle across the six cards. Desktop pointer only. */
const CARD_GRADIENTS: {
  image: string;
  motion: Motion;
  reverse?: boolean;
  seed?: number;
  tint?: string;
}[] = [
  { image: '16', motion: 'waveform' },
  {
    image: '16',
    motion: 'waveform',
    seed: 5.3,
    tint: 'hue-rotate(178deg) saturate(1.6) brightness(1.15)',
  },
  { image: '28', motion: 'waveform', reverse: true },
  {
    image: '28',
    motion: 'waveform',
    reverse: true,
    seed: 12.2,
    tint: 'hue-rotate(-100deg)',
  },
];

/**
 * "In the press" — six review cards (two rows of three) linking out to the
 * review. Phones show Engadget, Tech Advisor, and Wareable; the other three
 * stay in the list for tablet and desktop.
 * Figma: UH Landing Page / Homepage 2517:10248 (heading), 2517:10253 (grid).
 */
export const RingProUsPress = () => {
  const t = useTranslations('homepage');
  const hoverShaders = useHoverShaders();
  const [motionOk, setMotionOk] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: no-preference)');
    const sync = () => setMotionOk(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const shadersOn = hoverShaders && motionOk;
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [armed, setArmed] = useState<readonly boolean[]>(() =>
    PRESS_ITEMS.map(() => false),
  );

  return (
    <section
      className={styles.section}
      aria-labelledby="ring-pro-us-press-title"
    >
      <div className={styles.inner}>
        <header className={styles.head}>
          <p className={styles.eyebrow}>
            {t('homepage.ringProUs.press.eyebrow')}
          </p>
          <h2 id="ring-pro-us-press-title" className={styles.title}>
            {t('homepage.ringProUs.press.title')}
          </h2>
        </header>

        <ul className={styles.grid}>
          {PRESS_ITEMS.map((item, i) => {
            const gradient = CARD_GRADIENTS[i % CARD_GRADIENTS.length]!;
            const texture = textureById(gradient.image);
            const enter = () => {
              if (!shadersOn) return;
              setHoverIdx(i);
              setArmed((prev) =>
                prev[i] ? prev : prev.map((on, j) => (j === i ? true : on)),
              );
            };
            const leave = () =>
              setHoverIdx((current) => (current === i ? null : current));

            return (
              <li
                key={item.id}
                className={
                  PHONE_PRESS_IDS.has(item.id)
                    ? styles.cell
                    : `${styles.cell} ${styles.hideOnPhone}`
                }
                data-on={hoverIdx === i ? 'true' : undefined}
              >
                <a
                  className={styles.card}
                  onMouseEnter={enter}
                  onMouseLeave={leave}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-buttontype={`ring_pro_us_press_${item.id}`}
                >
                  {armed[i] ? (
                    <span
                      className={styles.shader}
                      style={
                        gradient.tint ? { filter: gradient.tint } : undefined
                      }
                      aria-hidden="true"
                    >
                      <GradientShader
                        className={styles.gradient}
                        src={texture.src}
                        seed={gradient.seed ?? i * 2.3 + 0.7}
                        params={{
                          ...CARD_SHADER,
                          motion: gradient.motion,
                          speed: gradient.reverse
                            ? -CARD_SHADER.speed
                            : CARD_SHADER.speed,
                        }}
                      />
                    </span>
                  ) : null}

                  <span className={styles.top}>
                    <OutletMark outlet={item.outlet} />
                    <q className={styles.quote}>
                      {t(`homepage.ringProUs.press.items.${item.id}.quote`)}
                    </q>
                  </span>

                  <span className={styles.read}>
                    {t('homepage.ringProUs.press.readStory')}
                    <span className={styles.srOnly}>
                      {t('homepage.ringProUs.press.aria.newTab')}
                    </span>
                    <svg
                      className={styles.readIcon}
                      viewBox="0 0 12 12"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M3.5 8.5 8.5 3.5M4.5 3.5h4v4"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default RingProUsPress;
